/**
 * BronzeStatueShowcase — UNC Tarheels Baseball bronze statue video carousel
 *
 * Plays photorealistic AI-generated bronze statue videos (Veo 3.1, 9:16)
 * featuring UNC baseball position players, coaches, and Rameses mascot.
 * Crossfade transitions between athlete clips with Carolina Blue accents.
 *
 * Timing: Event-driven via `ended` event — each video plays exactly once,
 * holds its last frame, then crossfades to the next. No `loop` attribute,
 * no setInterval. Safety timeout (15s) guards against `ended` not firing.
 *
 * Videos: public/videos/athletes/unc-{position}.mp4
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { C } from '../design-system/primitives/constants';
import { prefersReducedMotion } from '../../lib/animationConfig';
import bronzeAthletes from '../../data/bronzeAthletes.json';

const EASTER_EGG_ID = 'unc-rameses';
const baseCycle = bronzeAthletes
  .filter(({ id }) => id !== EASTER_EGG_ID)
  .map(({ id, label }) => ({ id, label }));
const easterEgg = bronzeAthletes.find(({ id }) => id === EASTER_EGG_ID);
const VIDEO_CYCLE = easterEgg
  ? [...baseCycle, { id: easterEgg.id, label: easterEgg.label }]
  : baseCycle;

const FADE_DURATION = 800; // ms crossfade
const PRELOAD_BEFORE_END = 2; // seconds before video end to preload next
const SAFETY_TIMEOUT = 15000; // max ms before forcing transition if ended doesn't fire

function videoSrc(id) {
  return `/videos/athletes/${id}.mp4`;
}

function posterSrc(id) {
  return `/images/athletes/bronze/${id}.png`;
}

function videoPathname(el) {
  try { return new URL(el.src, window.location.origin).pathname; }
  catch { return el.src; }
}

export default function BronzeStatueShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeSlot, setActiveSlot] = useState('A'); // 'A' or 'B'
  const [transitioning, setTransitioning] = useState(false);

  const videoARef = useRef(null);
  const videoBRef = useRef(null);
  const mountedRef = useRef(true);
  const preloadedRef = useRef(false);
  const safetyTimerRef = useRef(null);

  // Refs mirror state to avoid stale closures in event handlers
  const activeSlotRef = useRef('A');
  const currentIndexRef = useRef(0);
  const transitioningRef = useRef(false);

  activeSlotRef.current = activeSlot;
  currentIndexRef.current = currentIndex;
  transitioningRef.current = transitioning;

  const current = VIDEO_CYCLE[currentIndex];

  const getSlotRef = useCallback(
    (slot) => (slot === 'A' ? videoARef : videoBRef),
    []
  );

  // Preload next video into the inactive slot
  const preloadNext = useCallback(() => {
    if (preloadedRef.current) return;
    const slot = activeSlotRef.current;
    const idx = currentIndexRef.current;
    const nextIdx = (idx + 1) % VIDEO_CYCLE.length;
    const incomingSlot = slot === 'A' ? 'B' : 'A';
    const incomingVideo = getSlotRef(incomingSlot).current;
    if (incomingVideo) {
      const nextSrc = videoSrc(VIDEO_CYCLE[nextIdx].id);
      if (videoPathname(incomingVideo) !== nextSrc) {
        incomingVideo.src = nextSrc;
        incomingVideo.poster = posterSrc(VIDEO_CYCLE[nextIdx].id);
        incomingVideo.load();
      }
      preloadedRef.current = true;
    }
  }, [getSlotRef]);

  // Execute crossfade to next video
  const cycleNext = useCallback(() => {
    if (prefersReducedMotion()) return;
    if (!mountedRef.current) return;
    if (transitioningRef.current) return; // guard against double-fire

    const slot = activeSlotRef.current;
    const idx = currentIndexRef.current;
    const nextIdx = (idx + 1) % VIDEO_CYCLE.length;
    const incomingSlot = slot === 'A' ? 'B' : 'A';
    const incomingVideo = getSlotRef(incomingSlot).current;

    // Ensure incoming video has the right source loaded
    if (incomingVideo) {
      const targetSrc = videoSrc(VIDEO_CYCLE[nextIdx].id);
      if (videoPathname(incomingVideo) !== targetSrc) {
        incomingVideo.src = targetSrc;
        incomingVideo.poster = posterSrc(VIDEO_CYCLE[nextIdx].id);
        incomingVideo.load();
      }
      // Start playback so it's running during the crossfade
      incomingVideo.play().catch(() => {});
    }

    // Clear safety timer — we're transitioning now
    clearTimeout(safetyTimerRef.current);

    // Start crossfade
    setTransitioning(true);

    setTimeout(() => {
      if (!mountedRef.current) return;
      setCurrentIndex(nextIdx);
      setActiveSlot(incomingSlot);
      setTransitioning(false);
      preloadedRef.current = false;
    }, FADE_DURATION);
  }, [getSlotRef]);

  const handleVideoError = useCallback(() => {
    clearTimeout(safetyTimerRef.current);
    if (!transitioningRef.current) {
      setTimeout(() => {
        if (mountedRef.current) cycleNext();
      }, 500);
    }
  }, [cycleNext]);

  // Event-driven video lifecycle: ended + timeupdate + safety timer
  useEffect(() => {
    if (prefersReducedMotion()) return;

    const activeVideo = getSlotRef(activeSlot).current;
    if (!activeVideo) return;

    const handleEnded = () => {
      // Video finished naturally — trigger crossfade
      cycleNext();
    };

    const handleTimeUpdate = () => {
      // Preload next video when approaching end
      if (
        activeVideo.duration &&
        activeVideo.currentTime > activeVideo.duration - PRELOAD_BEFORE_END
      ) {
        preloadNext();
      }
    };

    activeVideo.addEventListener('ended', handleEnded);
    activeVideo.addEventListener('timeupdate', handleTimeUpdate);

    // Safety timeout in case ended doesn't fire (browser quirk, decode error)
    safetyTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !transitioningRef.current) {
        cycleNext();
      }
    }, SAFETY_TIMEOUT);

    return () => {
      activeVideo.removeEventListener('ended', handleEnded);
      activeVideo.removeEventListener('timeupdate', handleTimeUpdate);
      clearTimeout(safetyTimerRef.current);
    };
  }, [activeSlot, cycleNext, preloadNext, getSlotRef]);

  // Set initial video source on mount
  useEffect(() => {
    mountedRef.current = true;
    const initialVideo = videoARef.current;
    if (initialVideo) {
      initialVideo.src = videoSrc(VIDEO_CYCLE[0].id);
      initialVideo.poster = posterSrc(VIDEO_CYCLE[0].id);
      initialVideo.play().catch(() => {});
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reduced motion: show first frame only (video paused)
  const isReduced = prefersReducedMotion();

  // Compute opacity for each slot during transitions
  const slotAOpacity =
    activeSlot === 'A'
      ? transitioning ? 0 : 1
      : transitioning ? 1 : 0;
  const slotBOpacity =
    activeSlot === 'B'
      ? transitioning ? 0 : 1
      : transitioning ? 1 : 0;

  const videoStyle = (opacity) => ({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    opacity,
    transition: `opacity ${FADE_DURATION}ms ease-in-out`,
    zIndex: opacity > 0 ? 1 : 0,
  });

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: C.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          height: 'calc(100% - 40px)',
          aspectRatio: '9 / 16',
          maxWidth: 'calc(100% - 40px)',
          border: `2.5px solid ${C.goldMuted}80`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: `0 0 30px ${C.gold}18, 0 0 80px ${C.gold}0C, inset 0 0 40px ${C.gold}08`,
        }}
      >
      {/* Video slot A — no loop attribute, plays once then holds last frame */}
      <video
        ref={videoARef}
        style={videoStyle(slotAOpacity)}
        autoPlay={!isReduced}
        muted
        playsInline
        preload="auto"
        onError={handleVideoError}
      />

      {/* Video slot B — no loop attribute */}
      <video
        ref={videoBRef}
        style={videoStyle(slotBOpacity)}
        muted
        playsInline
        preload="auto"
        onError={handleVideoError}
      />

      {/* Sport label overlay */}
      <p
        style={{
          position: 'absolute',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          color: C.gold,
          fontWeight: 200,
          letterSpacing: '3px',
          fontSize: 13,
          textTransform: 'uppercase',
          opacity: transitioning ? 0 : 0.6,
          transition: 'opacity 0.6s ease',
          pointerEvents: 'none',
          userSelect: 'none',
          fontFamily: 'inherit',
          zIndex: 2,
        }}
      >
        {current.label}
      </p>

      </div>
    </div>
  );
}
